import { useEffect, useRef, useState } from "react"
import { format } from "date-fns"
import Modal from "react-modal"
import { toast, ToastContainer } from "react-toastify"
import { useLocation } from "react-router-dom"
import {
  FaGift, FaBan, FaTimes, FaCheckCircle, FaUsers,
  FaUsersCog, FaEnvelope, FaLock, FaPercentage,
  FaSearch, FaEye, FaUserCircle, FaBuilding,
  FaUserTie, FaUser, FaCalendarAlt, FaMapMarkerAlt,
  FaIdCard, FaCog, FaPaperPlane, FaSortAmountDown,
  FaSortAmountUp, FaFilter, FaDownload, FaSync
} from 'react-icons/fa'
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPaperPlane } from "@fortawesome/free-solid-svg-icons"
import { CloseOutlined } from "@ant-design/icons"
import { Button, Input, Tag, Tooltip, Badge, Dropdown, Menu, Select } from "antd"
import axios from "../../api/axios"

import LoaderAnnonce from "../../components/LoaderAnnonce"
import type { UserModel } from "../../models/UserModel"
import { getAllClientByUsers } from "../../services/Login_service"
import { changeUserRemise, updateFreeAnnouncement, updateUserInfo } from "../../services/User_service"
import DetailUser from "./detailUser/DetailUser"
import useAppContext from "../../hooks/useAppContext"
import type { AppContextType } from "../../context/AppContext"
import { paginate } from '../../utils/functions/pagination'
import { PaginationControls } from "../../components/PaginationControls"
import EmailModal from "./EmailModal"
import { LogoType } from "../../static/types/commonTypes/logoType"
import GestionCompteAdmin from "../../components/admin/GestionCompteAdmin"
import "../../styles/ListUser.css"
import Avatar from "../../image/avatar.png"

Modal.setAppElement("#root")

const ListUser = ({ status }: { status?: string }) => {
  const { logos } = useAppContext() as AppContextType

  const [clientUsers, setClientUsers] = useState<UserModel[]>([])
  const [adminUsers, setAdminUsers] = useState<UserModel[]>([])
  const [filteredClient, setFilteredUsers] = useState<UserModel[]>([])
  const [filteredAdmins, setFilteredAdmins] = useState<UserModel[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [searchTerm, setSearchTerm] = useState<string>("")
  const [adminSearchTerm, setAdminSearchTerm] = useState<string>("")
  const [selectedUser, setSelectedUser] = useState<UserModel | null>(null)
  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false)
  const [hasFreeAnnouncement, setHasFreeAnnouncement] = useState<boolean>(false)
  const [remiseNow, setRemiseNow] = useState<number>(0)
  const [modalContent, setModalContent] = useState<"details" | "liste">("details")
  const [activeTab, setActiveTab] = useState<"client" | "admin">("client")
  const hasVerified = useRef(false)
  const [userLogos, setUserLogos] = useState<LogoType[]>([])
  const location = useLocation()
  const userIdFromURL = new URLSearchParams(location.search).get("userId")
  const API_URL = process.env.REACT_APP_API_URL

  // Pagination
  const [currentPageClient, setCurrentPageClient] = useState(1)
  const [currentPageAdmin, setCurrentPageAdmin] = useState(1)
  const [pageSize, setPageSize] = useState<number>(20)

  const [selectedClient, setSelectedClient] = useState<UserModel | null>(null)
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false)
  const [emailAuthorized, setEmailAuthorized] = useState<boolean>(true)

  const [adminDetailOpen, setAdminDetailOpen] = useState(false)
  const [selectedAdminId, setSelectedAdminId] = useState<number | null>(null)

  // Filtres et tri
  const [statusFilter, setStatusFilter] = useState<string>("all")
  const [typeFilter, setTypeFilter] = useState<string>("all")
  const [sortField, setSortField] = useState<string>("createdAt")
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc")

  const handleOpenEmailModal = (client: UserModel) => {
    setSelectedClient(client)
    setIsEmailModalOpen(true)
  }

  const handleAdminDetail = (admin: UserModel) => {
    setSelectedAdminId(admin.id)
    setAdminDetailOpen(true)
  }

  const handleCloseEmailModal = () => {
    setSelectedClient(null)
    setIsEmailModalOpen(false)
  }

  const refreshUserLogos = async () => {
    if (selectedUser) {
      try {
        const response = await axios.get(`${API_URL}/logo/user/${selectedUser.id}`)
        setUserLogos(response.data.logos || [])
      } catch (error) {
        console.error('Erreur lors du rafraîchissement des logos:', error)
      }
    }
  }

  const getTypeLabel = (type: string) => {
    switch (type) {
      case "entreprise": return "Entreprise"
      case "professionnel": return "Professionnel"
      case "personne": return "Particulier"
      default: return type
    }
  }

  const getTypeIcon = (type: string) => {
    switch (type) {
      case "entreprise": return <FaBuilding className="me-1" />
      case "professionnel": return <FaUserTie className="me-1" />
      case "personne": return <FaUser className="me-1" />
      default: return <FaUserCircle className="me-1" />
    }
  }


  const filterUsers = (users: UserModel[], searchTerm: string) => {
    let filtered = users
    
    // Filtre par statut
    if (statusFilter !== "all") {
      filtered = filtered.filter(user => user.status === statusFilter)
    }
    
    // Filtre par type
    if (typeFilter !== "all") {
      if (typeFilter === "particulier") {
        filtered = filtered.filter(user => user.user_type === "personne")
      } else {
        filtered = filtered.filter(user => user.user_type === typeFilter)
      }
    }
    
    // Recherche texte
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim()
      filtered = filtered.filter((user) => {
        const fullName = `${user.firstname} ${user.name}`.toLowerCase()
        return [
          fullName.includes(searchLower),
          user.email.toLowerCase().includes(searchLower),
          user.reference?.toLowerCase().includes(searchLower),
          user.adresse?.toLowerCase().includes(searchLower),
          user.user_type.toLowerCase().includes(searchLower),
        ].some(Boolean)
      })
    }
    
    // Tri
    filtered.sort((a: any, b: any) => {
      let aVal = a[sortField]
      let bVal = b[sortField]
      
      // Gestion des dates
      if (sortField === "createdAt") {
        aVal = new Date(aVal).getTime()
        bVal = new Date(bVal).getTime()
      }
      
      // Gestion des chaînes
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase()
        bVal = bVal.toLowerCase()
      }
      
      if (sortOrder === "asc") {
        return aVal > bVal ? 1 : -1
      }
      return aVal < bVal ? 1 : -1
    })
    
    return filtered
  }

  const getListUsers = async () => {
    setLoading(true)
    const userId = localStorage.getItem("userId")
    if (userId) {
      try {
        let users = await getAllClientByUsers(Number.parseInt(userId))
        if (status) users = users.filter((u) => u.status === status)

        const clients = users.filter((u) => u.role !== "ADMIN" && u.status !== "anonymous")
        const admins = users.filter((u) => u.role === "ADMIN")

        setClientUsers(clients)
        setAdminUsers(admins)
        setFilteredUsers(filterUsers(clients, searchTerm))
        setFilteredAdmins(filterUsers(admins, adminSearchTerm))
      } catch (error) {
        console.error("Erreur lors de la récupération des utilisateurs", error)
      } finally {
        setLoading(false)
      }
    }
  }

  const handleUserUpdate = (updatedUser: UserModel) => {
    console.log('📝 [ListUser] Mise à jour utilisateur:', {
      id: updatedUser.id,
      user_type: updatedUser.user_type,
      role: updatedUser.role
    });

    const userId = Number(updatedUser.id);
    
    setClientUsers(prev => {
      if (updatedUser.role === 'ADMIN') {
        return prev.filter(u => u.id !== userId);
      }
      
      const exists = prev.some(u => u.id === userId);
      if (exists) {
        return prev.map(u => u.id === userId ? { ...u, ...updatedUser } : u);
      } else if (updatedUser.status !== 'anonymous') {
        return [updatedUser, ...prev];
      }
      return prev;
    });

    setAdminUsers(prev => {
      if (updatedUser.role !== 'ADMIN') {
        return prev.filter(u => u.id !== userId);
      }
      
      const exists = prev.some(u => u.id === userId);
      if (exists) {
        return prev.map(u => u.id === userId ? { ...u, ...updatedUser } : u);
      } else {
        return [updatedUser, ...prev];
      }
    });

    setSelectedUser(prev => {
      if (prev && prev.id === userId) {
        return { ...prev, ...updatedUser };
      }
      return prev;
    });
  };

  useEffect(() => { getListUsers() }, [])
  useEffect(() => { 
    setFilteredUsers(filterUsers(clientUsers, searchTerm))
    setCurrentPageClient(1) 
  }, [searchTerm, clientUsers, statusFilter, typeFilter, sortField, sortOrder])
  
  useEffect(() => { 
    setFilteredAdmins(filterUsers(adminUsers, adminSearchTerm))
    setCurrentPageAdmin(1) 
  }, [adminSearchTerm, adminUsers])

  useEffect(() => {
    if (userIdFromURL && !hasVerified.current) {
      const user = [...clientUsers, ...adminUsers].find((u) => u.id === Number.parseInt(userIdFromURL, 10))
      if (user) {
        setSelectedUser(user)
        setHasFreeAnnouncement(user.hasFreeAnnouncement || false)
        setModalIsOpen(true)
      }
      hasVerified.current = true
    }
  }, [userIdFromURL, clientUsers, adminUsers])

  useEffect(() => {
    if (selectedUser) setHasFreeAnnouncement(selectedUser.hasFreeAnnouncement || false)
  }, [selectedUser])

  useEffect(() => {
    const handleUserUpdatedEvent = (event: CustomEvent) => {
      const updatedUser = event.detail
      if (updatedUser && updatedUser.id) {
        handleUserUpdate(updatedUser)
      }
    }

    window.addEventListener('userUpdated', handleUserUpdatedEvent as EventListener)
    
    return () => {
      window.removeEventListener('userUpdated', handleUserUpdatedEvent as EventListener)
    }
  }, [])

  useEffect(() => {
    if (selectedUser) {
      refreshUserLogos()
    }
  }, [selectedUser?.id])


useEffect(() => {
  // Fermer toutes les modales lors du changement d'onglet
  setModalIsOpen(false)
  setSelectedUser(null)
  setIsEmailModalOpen(false)
  setSelectedClient(null)
  setAdminDetailOpen(false)
  setSelectedAdminId(null)
}, [activeTab])
  const handleUserDetail = (user: UserModel) => {
    setRemiseNow(user.referralPercentage)
    setSelectedUser(user)
    setHasFreeAnnouncement(user.hasFreeAnnouncement || false)
    setIsEmailModalOpen(user.email_autorized || true)
    setModalContent("details")
    setModalIsOpen(true)
  }

  const handleCloseDetail = () => {
    setModalIsOpen(false)
    setSelectedUser(null)
    setModalContent("details")
  }

  const saveRemiseChange = async () => {
    setLoading(true)
    if (selectedUser) {
      await changeUserRemise(selectedUser.id, remiseNow)
      getListUsers()
    }
    setModalIsOpen(false)
    setLoading(false)
  }

  const activateUser = async (userId: number) => {
    try {
      setLoading(true)
      const response = await updateUserInfo(userId, { status: "active" })
      setLoading(false)
      if (response.status === 200) {
        toast.success("L'utilisateur a été activé !")
        
        if (selectedUser && selectedUser.id === userId) {
          const updatedUser = { ...selectedUser, status: "active" }
          handleUserUpdate(updatedUser)
        }
      }
    } catch (error) {
      console.error("Erreur d'activation", error)
      toast.error("Erreur lors de l'activation")
      setLoading(false)
    }
  }

  const deactivateUser = async (userId: number) => {
    try {
      setLoading(true)
      const response = await updateUserInfo(userId, { status: "inactive" })
      setLoading(false)
      if (response.status === 200) {
        toast.success("L'utilisateur a été désactivé !")
        
        if (selectedUser && selectedUser.id === userId) {
          const updatedUser = { ...selectedUser, status: "inactive" }
          handleUserUpdate(updatedUser)
        }
      }
    } catch (error) {
      console.error("Erreur de désactivation", error)
      toast.error("Erreur lors de la désactivation")
      setLoading(false)
    }
  }

  const handleUpdateFreeAnnouncement = async (userId: number, hasFree: boolean) => {
    try {
      setLoading(true)
      const response = await updateFreeAnnouncement(userId, hasFree)
      setLoading(false)
      if (response?.status === 200) {
        setHasFreeAnnouncement(hasFree)
        
        if (selectedUser && selectedUser.id === userId) {
          const updatedUser = { ...selectedUser, hasFreeAnnouncement: hasFree }
          handleUserUpdate(updatedUser)
        }
        
        toast.success(hasFree ? "Annonce gratuite activée !" : "Annonce gratuite désactivée !")
      }
    } catch (error) {
      console.error("Erreur annonce gratuite", error)
      toast.error("Erreur attribution annonce gratuite")
      setLoading(false)
    }
  }

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc")
    } else {
      setSortField(field)
      setSortOrder("desc")
    }
  }

  const clearFilters = () => {
    setStatusFilter("all")
    setTypeFilter("all")
    setSearchTerm("")
    setAdminSearchTerm("")
    setSortField("createdAt")
    setSortOrder("desc")
  }

  const exportToCSV = () => {
    // Fonction d'export CSV simplifiée
    const data = activeTab === "client" ? filteredClient : filteredAdmins
    const headers = ["Nom", "Prénom", "Email", "Type", "Statut", "Date d'inscription"]
    const csvContent = [
      headers.join(","),
      ...data.map(user => [
        user.name,
        user.firstname,
        user.email,
        getTypeLabel(user.user_type),
        user.status,
        format(new Date(user.createdAt), "dd/MM/yyyy")
      ].join(","))
    ].join("\n")
    
    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${activeTab === "client" ? "clients" : "admins"}_${new Date().toISOString().split('T')[0]}.csv`
    a.click()
  }

  if (loading) return <LoaderAnnonce />

  const { paginatedData: paginatedClients, totalPages: totalPagesClient } = paginate(filteredClient, currentPageClient, pageSize)
  const { paginatedData: paginatedAdmins, totalPages: totalPagesAdmin } = paginate(filteredAdmins, currentPageAdmin, pageSize)

  const sortMenu = (
    <Menu>
      <Menu.Item key="name" onClick={() => handleSort("name")}>
        Nom {sortField === "name" && (sortOrder === "asc" ? <FaSortAmountUp /> : <FaSortAmountDown />)}
      </Menu.Item>
      <Menu.Item key="createdAt" onClick={() => handleSort("createdAt")}>
        Date d'inscription {sortField === "createdAt" && (sortOrder === "asc" ? <FaSortAmountUp /> : <FaSortAmountDown />)}
      </Menu.Item>
      <Menu.Item key="email" onClick={() => handleSort("email")}>
        Email {sortField === "email" && (sortOrder === "asc" ? <FaSortAmountUp /> : <FaSortAmountDown />)}
      </Menu.Item>
    </Menu>
  )

  return (
    <div className="list-user-container">
      <ToastContainer />

      {/* Header avec statistiques */}
      <div className="dashboard-header mb-4">
        <div className="header-title">
          <h1>
            {activeTab === "client" ? <><FaUsers /> Gestion des Clients</> : <><FaUsersCog /> Gestion des Administrateurs</>}
          </h1>
          <div className="stats-badge" style={{color: activeTab === "client" ? '#4BB0C0' : '#2A919D', fontWeight: 'bold',}}>
            <Badge 
              count={activeTab === "client" ? clientUsers.length : adminUsers.length} 
              style={{ 
                backgroundColor: activeTab === "client" ? '#4BB0C0' : '#2A919D',
              fontWeight: 'bold',
              }}
              showZero
            />
          </div>
        </div>
        
        <div className="header-actions">
          <Tooltip title="Actualiser">
            <Button icon={<FaSync />} onClick={getListUsers} type="text" />
          </Tooltip>
          <Tooltip title="Exporter en CSV">
            <Button icon={<FaDownload />} onClick={exportToCSV} type="text" />
          </Tooltip>
        </div>
      </div>

      {/* Onglets */}
      <div className="tab-navigation mb-4">
        <div className={`tab ${activeTab === "client" ? "active" : ""}`} onClick={() => setActiveTab("client")}>
          <FaUsers className="me-2" />
          Clients
          <span className="tab-count">{clientUsers.length}</span>
        </div>
        <div className={`tab ${activeTab === "admin" ? "active" : ""}`} onClick={() => setActiveTab("admin")}>
          <FaUsersCog className="me-2" />
          Administrateurs
          <span className="tab-count">{adminUsers.length}</span>
        </div>
      </div>

      {/* Barre de recherche et filtres */}
      <div className="filter-bar mb-4">
        <div className="row g-3 align-items-center">
          <div className="col-md-4">
            <Input
              prefix={<FaSearch className="text-muted" />}
              placeholder={
                activeTab === "client" 
                  ? "Rechercher un client..." 
                  : "Rechercher un administrateur..."
              }
              value={activeTab === "client" ? searchTerm : adminSearchTerm}
              onChange={(e) => activeTab === "client" ? setSearchTerm(e.target.value) : setAdminSearchTerm(e.target.value)}
              size="large"
              allowClear
              className="search-input"
            />
          </div>
          
          {activeTab === "client" && (
            <>
              <div className="col-md-2">
                <Select
                  placeholder="Statut"
                  value={statusFilter}
                  onChange={setStatusFilter}
                  className="w-100"
                  size="large"
                >
                  <Select.Option value="all">Tous les statuts</Select.Option>
                  <Select.Option value="active">Actif</Select.Option>
                  <Select.Option value="inactive">Inactif</Select.Option>
                </Select>
              </div>
              
              <div className="col-md-2">
                <Select
                  placeholder="Type"
                  value={typeFilter}
                  onChange={setTypeFilter}
                  className="w-100"
                  size="large"
                >
                  <Select.Option value="all">Tous les types</Select.Option>
                  <Select.Option value="entreprise">Entreprise</Select.Option>
                  <Select.Option value="professionnel">Professionnel</Select.Option>
                  <Select.Option value="particulier">Particulier</Select.Option>
                </Select>
              </div>
            </>
          )}
          
          <div className="col-md-2">
            <Dropdown overlay={sortMenu} trigger={['click']}>
              <Button icon={<FaSortAmountDown />} size="large" className="w-100">
                Trier
              </Button>
            </Dropdown>
          </div>
          
          <div className="col-md-2">
            <Button 
              icon={<FaFilter />} 
              onClick={clearFilters}
              size="large"
              className="w-100"
              disabled={statusFilter === "all" && typeFilter === "all" && !searchTerm && !adminSearchTerm}
            >
              Réinitialiser
            </Button>
          </div>
        </div>
      </div>

      {/* Table Clients */}
      {activeTab === "client" && (
        <>
          <div
            className="table-wrapper"
            style={{
              maxHeight: "600px",    // hauteur max avant scroll vertical
              overflowY: "auto",     // scroll vertical
              overflowX: "auto",     // scroll horizontal
            }}
          >
            <table className="simple-table" style={{ minWidth: "800px" }}>
              <thead style ={{backgroundColor: "#4bb0c0"}}>
                <tr>
                  <th style ={{backgroundColor: "#4bb0c0"}}>Client</th>
                  {/* <th style ={{backgroundColor: "#4bb0c0"}}>Référence</th> */}
                  <th style ={{backgroundColor: "#4bb0c0"}}>Adresse</th>
                  <th style ={{backgroundColor: "#4bb0c0"}}>Date d'inscription</th>
                  <th style ={{backgroundColor: "#4bb0c0"}}>Type</th>
                  <th style ={{backgroundColor: "#4bb0c0"}}>Annonce gratuite</th>
                  <th style ={{backgroundColor: "#4bb0c0"}}>Limite de Remise</th>
                  <th style ={{backgroundColor: "#4bb0c0"}}>Email</th>
                  <th style ={{backgroundColor: "#4bb0c0"}}>Statut</th>
                  <th style ={{backgroundColor: "#4bb0c0"}}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedClients.length > 0 ? (
                  paginatedClients.map((client) => (
                    <tr key={client.id}>
                      {/* Nom et email */}
                    <td>
                      <div
                        style={{
                          display: "flex",
                          gap: "15px",
                        }}
                      >
                        <img
                        src={
                          client.profile_picture
                            ? `${process.env.REACT_APP_API_BASE}/${client.profile_picture}`
                            : Avatar
                        }
                        alt={`${client.firstname} ${client.name}`}
                        style={{
                          width: "60px",
                          height: "60px",
                          borderRadius: "50%",
                          objectFit: "cover",
                        }}
                      />


                        <div>
                          <div className="client-name" style={{color:"#4bb0c0"}}>
                            {client.firstname} {client.name}
                          </div>
                          <div className="client-email">{client.email}</div>
                        </div>
                      </div>
                    </td>  
                      {/* Référence */}
                      {/* <td style ={{textAlign:"center",justifyContent:"center"}}>
                        <div className="reference">
                          <FaIdCard className="icon" /> {client.reference || "-"}
                        </div>
                      </td> */}
                      
                      {/* Adresse */}
                      <td style ={{textAlign:"center",justifyContent:"center"}}>
                        <Tooltip 
                          title={`${client.adresse || "-"} ${client.country || "-"} ${client.postal_code || "-"}`}
                          placement="topLeft"
                        >
                          <div className="address" style={{
                            maxWidth: "200px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            cursor: "help"
                          }}>
                            <FaMapMarkerAlt className="icon" style={{marginRight: "6px"}} /> 
                            {client.postal_code || "-"} {client.adresse?.split(' ').slice(0, 2).join(' ') || "-"}
                          </div>
                        </Tooltip>
                      </td>
                      
                      {/* Date */}
                      <td style ={{textAlign:"center",justifyContent:"center"}}>
                        <div className="date">
                          <FaCalendarAlt className="icon" />
                          {format(new Date(client.createdAt), "dd/MM/yyyy")}
                        </div>
                      </td>
                      
                      {/* Type */}
                      <td style ={{textAlign:"center",justifyContent:"center"}}>
                        <div className="user-type">
                          {getTypeIcon(client.user_type)}
                          <span>{getTypeLabel(client.user_type)}</span>
                        </div>
                      </td>
                      
                      {/* Annonce gratuite */}
                      <td style ={{textAlign:"center",justifyContent:"center"}}>
                        {client.hasFreeAnnouncement ? (
                          <div className="free-announcement yes" title ="Annonce gratuite active">
                            <FaGift className="icon"  style ={{color:"green",fontSize:"20px"}}/> 
                          </div>
                        ) : (
                          <div className="free-announcement no" title ="Pas d'annonce gratuite">
                            <FaTimes className="icon" style={{color:"red",fontSize:"20px"}} />
                          </div>
                        )}
                      </td>
                      
                      {/* Remise */}
                      <td style ={{textAlign:"center",justifyContent:"center"}}>
                        <div className={`discount ${(client as any).discount_limit_percentage ? 'active' : 'inactive'}`}>
                          <FaPercentage className="icon" />
                          {(client as any).discount_limit_percentage || 0}%
                        </div>
                      </td>
                      
                      {/* Email */}
                      <td style ={{textAlign:"center",justifyContent:"center"}}>
                        {client.email_autorized === false ? (
                          <div className="email-status blocked" title ="Email non autorisé">
                            <FaLock className="icon"  style={{color:"red",fontSize:"20px"}}/> 
                          </div>
                        ) : (
                          <div className="email-status allowed" title ="Email autorisé">
                            <FaCheckCircle className="icon" style ={{color:"green",fontSize:"20px"}} /> 
                          </div>
                        )}
                      </td>
                      
                      {/* Statut */}
                      <td className="text-center">
                        {client.status === "active" && (
                          <i
                            className="bi bi-check-circle-fill text-success"
                            title="Actif"
                          ></i>
                        )}

                        {client.status === "inactive" && (
                          <i
                            className="bi bi-pause-circle-fill text-secondary"
                            title="Inactif"
                          ></i>
                        )}

                        {client.status === "rejected" && (
                          <i
                            className="bi bi-x-circle-fill text-danger"
                            title="Rejeté"
                          ></i>
                        )}

                        {!["active", "inactive", "rejected"].includes(client.status) && (
                          <i
                            className="bi bi-question-circle-fill text-muted"
                            title="Indéfini"
                          ></i>
                        )}
                      </td>

                      
                      {/* Actions */}
                      <td style ={{textAlign:"center",justifyContent:"center"}}>
                        <div className="actions">
                          <button 
                            onClick={() => handleOpenEmailModal(client)}
                            className="action-btn email"
                            title="Envoyer un email"
                          >
                            <FontAwesomeIcon icon={faPaperPlane} />
                          </button>
                          <button 
                            onClick={() => handleUserDetail(client)}
                            className="action-btn detail"
                            title="Voir les détails"
                          >
                            <FaEye /> Détail
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={10} className="no-data">
                      <FaUsers size={48} />
                      <div>Aucun client trouvé</div>
                      <button onClick={clearFilters} className="reset-btn">
                        Réinitialiser les filtres
                      </button>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="table-footer">
            <div className="row align-items-center">
              <div className="col-md-6">
                <div className="results-info">
                  Affichage de <strong>{paginatedClients.length}</strong> sur <strong>{filteredClient.length}</strong> clients
                </div>
              </div>
              <div className="col-md-6">
                <div className="d-flex justify-content-end align-items-center gap-3">
                  <Select
                    value={pageSize}
                    onChange={setPageSize}
                    style={{ width: 120 }}
                    size="small"
                  >
                    <Select.Option value={10}>10 / page</Select.Option>
                    <Select.Option value={20}>20 / page</Select.Option>
                    <Select.Option value={50}>50 / page</Select.Option>
                    <Select.Option value={100}>100 / page</Select.Option>
                  </Select>
                  <PaginationControls
                    currentPage={currentPageClient}
                    totalPages={totalPagesClient}
                    onPageChange={setCurrentPageClient}
                  />
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Table Admins */}
      {activeTab === "admin" && (
        <>
          <div
            className="table-wrapper"
            style={{
              maxHeight: "600px",    // hauteur max avant scroll vertical
              overflowY: "auto",     // scroll vertical
              overflowX: "auto",     // scroll horizontal
            }}
          >
            <table className="simple-table" style={{ minWidth: "800px" }}>
            <thead>
              <tr>
                <th style={{ backgroundColor: "#4bb0c0" }}>Administrateur</th>
                <th style ={{backgroundColor: "#4bb0c0"}}>Référence</th>
                <th style ={{backgroundColor: "#4bb0c0"}}>Adresse</th>
                <th style ={{backgroundColor: "#4bb0c0"}}>Date d'inscription</th>
                <th style ={{backgroundColor: "#4bb0c0"}}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedAdmins.length > 0 ? (
                paginatedAdmins.map((admin) => (
                  <tr key={admin.id}>
                    {/* Nom et email */}
                    <td style ={{textAlign:"center",justifyContent:"center"}}>
                      <div className="admin-info">
                        <div className="admin-name">
                          {admin.firstname} {admin.name}
                          <span className="admin-badge">ADMIN</span>
                        </div>
                        <div className="admin-email">{admin.email}</div>
                      </div>
                    </td>
                    
                    {/* Référence */}
                    <td style ={{textAlign:"center",justifyContent:"center"}}>
                      <div className="reference">
                        <FaIdCard className="icon" /> {admin.reference || "-"}
                      </div>
                    </td>
                    
                    {/* Adresse */}
                    <td style ={{textAlign:"center",justifyContent:"center"}}>
                      <div className="address">
                        <FaMapMarkerAlt className="icon" /> {admin.adresse || "-"}
                      </div>
                    </td>
                    
                    {/* Date */}
                    <td style ={{textAlign:"center",justifyContent:"center"}}>
                      <div className="date">
                        <FaCalendarAlt className="icon" />
                        {format(new Date(admin.createdAt), "dd/MM/yyyy")}
                      </div>
                    </td>
                    
                    {/* Actions */}
                    <td style ={{textAlign:"center",justifyContent:"center"}}>
                      <button 
                        onClick={() => handleAdminDetail(admin)}
                        className="action-btn detail"
                        title="Voir les détails"
                      >
                        <FaEye /> Détails
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="no-data">
                    <FaUsersCog size={48} />
                    <div>Aucun administrateur trouvé</div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

          <div className="table-footer">
            <div className="row align-items-center">
              <div className="col-md-6">
                <div className="results-info">
                  Affichage de <strong>{paginatedAdmins.length}</strong> sur <strong>{filteredAdmins.length}</strong> administrateurs
                </div>
              </div>
              <div className="col-md-6">
                <div className="d-flex justify-content-end align-items-center gap-3">
                  <Select
                    value={pageSize}
                    onChange={setPageSize}
                    style={{ width: 120 }}
                    size="small"
                  >
                    <Select.Option value={10}>10 / page</Select.Option>
                    <Select.Option value={20}>20 / page</Select.Option>
                    <Select.Option value={50}>50 / page</Select.Option>
                  </Select>
                  <PaginationControls
                    currentPage={currentPageAdmin}
                    totalPages={totalPagesAdmin}
                    onPageChange={setCurrentPageAdmin}
                  />
                </div>
              </div>
            </div>
          </div>
        </>
      )}

{/* MODALES - EN DEHORS DES BLOCS CONDITIONNELS */}
      
      {/* Modal Email */}
      {isEmailModalOpen && selectedClient && activeTab === "client" && (
        <EmailModal
          key={`email-${selectedClient.id}`}
          show={isEmailModalOpen}
          onClose={handleCloseEmailModal}
          recipientName={`${selectedClient.firstname} ${selectedClient.name}`}
          recipientEmail={selectedClient.email}
          currentUserEmail={localStorage.getItem("userEmail") || ""}
          fromUserListPoint={false}
          minAnnonceAmount={false}
        />
      )}

      {/* Modal Admin */}
      {adminDetailOpen && selectedAdminId && activeTab === "admin" && (
        <Modal
          key={`admin-${selectedAdminId}`}
          isOpen={adminDetailOpen}
          onRequestClose={() => setAdminDetailOpen(false)}
          contentLabel="Détails administrateur"
          portalClassName="admin-modal-portal"
          style={{
            overlay: {
              backgroundColor: "rgba(0,0,0,0.55)",
              zIndex: 1300,
            },
            content: {
              position: "fixed",
              top: "50%",
              left: "50%",
              right: "auto",
              bottom: "auto",
              marginRight: "-50%",
              transform: "translate(-50%, -50%)",
              width: "90%",
              maxWidth: "1100px",
              height: "90vh",
              padding: 0,
              overflow: "hidden",
              borderRadius: "14px",
              border: "none",
              boxShadow: "0 12px 40px rgba(0,0,0,0.25)",
            },
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
              backgroundColor: "#fff",
            }}
          >
            <div
              style={{
                padding: "14px 20px",
                borderBottom: "1px solid #eee",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                backgroundColor: "#fafafa",
              }}
            >
              <span
                style={{
                  fontSize: "25px",
                  fontWeight: 600,
                  color: "#2A919D",
                }}
              >
                Détails du compte administrateur
              </span>
              <Button
                icon={<CloseOutlined />}
                onClick={() => setAdminDetailOpen(false)}
                type="text"
              />
            </div>
            <div
              style={{
                flex: 1,
                overflowY: "auto",
                padding: "20px",
                backgroundColor: "#f6f8fa",
              }}
            >
              <GestionCompteAdmin
                userId={selectedAdminId}
                onClose={() => setAdminDetailOpen(false)}
              />
            </div>
            <div
              style={{
                padding: "14px 20px",
                borderTop: "1px solid #eee",
                display: "flex",
                justifyContent: "flex-end",
                backgroundColor: "#fafafa",
              }}
            >
              <Button
                onClick={() => setAdminDetailOpen(false)}
                icon={<CloseOutlined />}
              >
                Fermer
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Modal Client */}
      {modalIsOpen && selectedUser && activeTab === "client" && (
        <Modal
          key={`client-${selectedUser.id}`}
          isOpen={modalIsOpen}
          onRequestClose={handleCloseDetail}
          contentLabel="Détails de l'utilisateur"
          className="modern-modal"
          overlayClassName="modern-modal-overlay"
          portalClassName="client-modal-portal"
        >
          {modalContent === "details" && (
            <DetailUser
              logos={userLogos}
              user={selectedUser}
              remiseNow={remiseNow}
              setRemiseNow={setRemiseNow}
              saveRemiseChange={saveRemiseChange}
              onLogosUpdate={refreshUserLogos}
              onUserUpdate={handleUserUpdate}
            />
          )}
          {selectedUser.status === "active" && (
            <div className="row mx-0 mb-3">
              <div className="col-6 px-2">
                {hasFreeAnnouncement ? (
                  <button
                    onClick={() => handleUpdateFreeAnnouncement(selectedUser.id, false)}
                    className="btn btn-warning w-100"
                    style={{
                      backgroundColor: '#4BB0C0',
                      borderColor: '#4BB0C0',
                      color: '#fff',
                    }}>
                    <FaTimes className="me-2" /> Désactiver l'annonce gratuite
                  </button>
                ) : (
                  <button
                    onClick={() => handleUpdateFreeAnnouncement(selectedUser.id, true)}
                    className="btn lst-btn-gratuit w-100"
                    style={{
                      backgroundColor: '#2A919D',
                      borderColor: '#2A919D',
                      color: '#fff',
                    }}
                  >
                    <FaGift className="me-2" /> Annonce gratuite
                  </button>
                )}
              </div>
              <div className="col-6 px-2">
                <button onClick={() => deactivateUser(selectedUser.id)} className="btn btn-danger w-100">
                  <FaBan className="me-2" /> Désactiver
                </button>
              </div>
            </div>
          )}

          {selectedUser.status === "inactive" && (
            <div className="row mx-0 mb-3">
              <div className="col-12">
                <button onClick={() => activateUser(selectedUser.id)} className="btn btn-success w-100">
                  <FaCheckCircle className="me-2" /> Réactiver
                </button>
              </div>
            </div>
          )}

          <div className="p-3 border-top text-center bg-light">
            <button onClick={handleCloseDetail} className="btn btn-outline-secondary">
              <i className="bi bi-box-arrow-left me-2"></i> Fermer
            </button>
          </div>
        </Modal>
      )}
    </div>
  )
}

export default ListUser